import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Root element not found");

const BUILD_REFRESH_KEY = "app-build-refresh";
const CACHE_RESET_REFRESH_KEY = "app-cache-reset-refresh";

const renderApp = () => {
  createRoot(rootElement).render(<App />);
};

function getReloadUrl(params: Record<string, string>) {
  const url = new URL(window.location.href);

  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.set(key, value);
  });

  return url.toString();
}

function getVersionedUrl(buildId: string) {
  return getReloadUrl({ v: buildId });
}

async function ensureLatestBuild() {
  if (typeof window === "undefined") return false;

  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, {
      cache: "reload",
      headers: {
        "cache-control": "no-cache",
        pragma: "no-cache",
      },
    });

    if (!response.ok) {
      return false;
    }

    const data = (await response.json()) as { buildId?: string };
    const latestBuildId = data.buildId;

    if (!latestBuildId || latestBuildId === __APP_BUILD_ID__) {
      sessionStorage.removeItem(BUILD_REFRESH_KEY);
      return false;
    }

    if (sessionStorage.getItem(BUILD_REFRESH_KEY) === latestBuildId) {
      return false;
    }

    sessionStorage.setItem(BUILD_REFRESH_KEY, latestBuildId);
    window.location.replace(getVersionedUrl(latestBuildId));
    return true;
  } catch (error) {
    console.warn("[Cache] Failed to validate latest build:", error);
    return false;
  }
}

async function clearLegacyPwaCache() {
  if (typeof window === "undefined") return false;

  let clearedSomething = false;

  try {
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      if (registrations.length > 0) {
        clearedSomething = true;
      }
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }

    if ("caches" in window) {
      const cacheKeys = await caches.keys();
      if (cacheKeys.length > 0) {
        clearedSomething = true;
      }
      await Promise.all(cacheKeys.map((cacheKey) => caches.delete(cacheKey)));
    }
  } catch (error) {
    console.warn("[Cache] Failed to clear legacy caches:", error);
  }

  return clearedSomething;
}

async function bootstrap() {
  const clearedLegacyCache = await clearLegacyPwaCache();

  if (clearedLegacyCache && sessionStorage.getItem(CACHE_RESET_REFRESH_KEY) !== "done") {
    sessionStorage.setItem(CACHE_RESET_REFRESH_KEY, "done");
    window.location.replace(getReloadUrl({ refresh: String(Date.now()) }));
    return;
  }

  const redirectedToLatestBuild = await ensureLatestBuild();
  if (redirectedToLatestBuild) return;

  sessionStorage.removeItem(CACHE_RESET_REFRESH_KEY);
  renderApp();
}

void bootstrap();